import { useMemo, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import SwitchInput from 'components/field/SwitchInput';
import TimeInput from 'components/field/TimeInput';
import Card from 'components/global/Card';
import Collapse from 'components/global/Collapse';
import Icon from 'components/global/Icon';
import Tag from 'components/global/Tag';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';

import * as S from './styled/WorkingHourTable.styled';

export const workingHoursDummyData = [
	{
		status: true,
		day: 'sunday',
		from: '00:00',
		to: '23:59',
		shift: [
			{
				start: '09:00',
				end: '18:00'
			}
		]
	},
	{
		status: true,
		day: 'monday',
		from: '00:00',
		to: '23:59',
		shift: [
			{
				start: '09:00',
				end: '18:00'
			}
		]
	},
	{
		status: true,
		day: 'tuesday',
		from: '00:00',
		to: '23:59',
		shift: [
			{
				start: '09:00',
				end: '18:00'
			}
		]
	},
	{
		status: true,
		day: 'wednesday',
		from: '00:00',
		to: '23:59',
		shift: [
			{
				start: '09:00',
				end: '18:00'
			}
		]
	},
	{
		status: true,
		day: 'thursday',
		from: '00:00',
		to: '23:59',
		shift: [
			{
				start: '09:00',
				end: '18:00'
			}
		]
	},
	{
		status: false,
		day: 'friday',
		from: '00:00',
		to: '23:59',
		shift: [
			{
				start: '09:00',
				end: '18:00'
			}
		]
	},
	{
		status: false,
		day: 'saturday',
		from: '00:00',
		to: '23:59',
		shift: [
			{
				start: '09:00',
				end: '18:00'
			}
		]
	}
];

const WorkingHours = ({ data, mode, index }) => {
	const {
		watch,
		control,
		setValue,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = useFormContext();
	const totalAccordionRef = useRef(null);
	const isViewMode = mode === 'view';
	const isLoading = isFormLoading || isSubmitting;

	// Assign workingHours watch to a variable
	const workingHours = watch(`workingHours[${index}]`);

	const {
		fields: workingHoursFields,
		append,
		remove
	} = useFieldArray({
		control,
		name: `workingHours[${index}].shift`
	});

	const renderTimeInput = (fieldName) => (
		<TimeInput
			name={fieldName}
			disabled={isViewMode || !workingHours.status}
			value={watch(fieldName)}
			setValue={(value) => setValue(fieldName, value, { shouldValidate: true, shouldDirty: true })}
		/>
	);

	const whMappingColumns = useMemo(
		() => [
			{
				header: 'start',
				accessorKey: 'start',
				showOnMobile: true,
				renderCell: ({ row: { index: whIndex } }) =>
					renderTimeInput(`workingHours[${index}].shift[${whIndex}].start`)
			},
			{
				header: 'end',
				accessorKey: 'end',
				showOnMobile: true,
				renderCell: ({ row: { index: whIndex } }) =>
					renderTimeInput(`workingHours[${index}].shift[${whIndex}].end`)
			},
			{ id: 'deleteRow', showOnMobile: true, size: 20, maxSize: 20 }
		],
		[index, isViewMode, workingHours.status]
	);

	const renderShiftsTags = () =>
		workingHours.status && (
			<S.Flex>
				{workingHours.shift?.map(
					(shift) =>
						shift.end && (
							<Tag
								className="mx-2"
								variant="disabled"
								key={shift.start}
								label={`${shift.start}-${shift.end}`}
							/>
						)
				)}
			</S.Flex>
		);

	return (
		<Card rounded fullWidth className="mb-3" padding={1.5}>
			<S.FlexSpaced className="day" ref={totalAccordionRef}>
				<S.Flex>
					<SwitchInput name={`workingHours[${index}].status`} disabled={isViewMode} />
					<S.DayName>
						<Typography fontWeight="500">{data.day}</Typography>
					</S.DayName>
					{renderShiftsTags()}
				</S.Flex>
				<Icon className="expand-icon" icon={faAngleDown} size="medium" />
			</S.FlexSpaced>
			<Collapse buttonRef={totalAccordionRef}>
				<div className="my-4">
					<Table
						loading={isLoading}
						data={workingHoursFields}
						columns={whMappingColumns}
						onRowDelete={(row) => remove(row.index)}
						onRowAdd={() => append({})}
						showAddNewRowButton={!isViewMode}
						initEditMode={(row) => row.id === null}
						isRowDeletable={() => !isViewMode}
					/>
				</div>
			</Collapse>
		</Card>
	);
};

const WorkingHourTable = ({ mode }) => {
	const { watch } = useFormContext();
	return (
		<>
			{watch('workingHours')?.map((data, index) => (
				<WorkingHours key={data.day} data={data} mode={mode} index={index} />
			))}
		</>
	);
};

export default WorkingHourTable;
